import { Tooltip } from "antd";
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from "react";
import { cn } from "../../utils/cn";

const BASE_CLASSES =
  "inline-flex w-max max-w-full items-center gap-2 rounded-[2px] bg-gray1 px-2 py-1 text-[12.44px] font-medium leading-[normal] text-gray9";
const ACTION_CLASSES =
  "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50";

export interface CustomTagProps
  extends Omit<HTMLAttributes<HTMLElement>, "onClick">,
    Pick<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "disabled"> {
  children: ReactNode;
  icon?: ReactNode;
  tooltip?: ReactNode;
  /** Set when a parent supplies the click action or tooltip. */
  interactive?: boolean;
  /** Overrides the automatic interaction-based border. */
  bordered?: boolean;
}

/** Static labels render as spans; click actions render as native buttons. */
export function CustomTag({
  children,
  icon,
  tooltip,
  interactive = false,
  bordered,
  onClick,
  disabled = false,
  className,
  style,
  ...elementProps
}: CustomTagProps) {
  const hasTooltip = tooltip != null && tooltip !== false && tooltip !== "";
  const isClickable = typeof onClick === "function";
  const showBorder = bordered ?? (interactive || isClickable || hasTooltip);

  // Apply the border rule after consumer styles so the prop stays authoritative.
  const tagStyle: CSSProperties = {
    ...style,
    borderWidth: showBorder ? 1 : 0,
    borderStyle: "solid",
    borderColor: style?.borderColor ?? "currentColor",
  };
  const sharedProps = {
    ...elementProps,
    className: cn(BASE_CLASSES, isClickable && ACTION_CLASSES, className),
    style: tagStyle,
  };
  const content = (
    <>
      {icon && <span className="inline-flex shrink-0 items-center">{icon}</span>}
      <span className="min-w-0 break-words whitespace-normal">{children}</span>
    </>
  );
  const tag = isClickable ? (
    <button {...sharedProps} type="button" disabled={disabled} onClick={onClick}>
      {content}
    </button>
  ) : (
    <span {...sharedProps}>{content}</span>
  );

  return hasTooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag;
}

export default CustomTag;
